import type { Step, StepStatus } from '@lucid/lego';
import { Note, Prospect, ProspectStatusLog } from 'src/api/services/lupp/prospect/dto.prospect';
import { formatToOffsetDateTime, formatDateForPicker } from '../../../../utils/tools';
import {
  getClosedSubStatusOptions,
  getJunkPrimaryReasons,
  getNotInterestedPrimaryReasons,
  getSecondaryReasonsByPrimary,
} from '../../constants';

// Re-export for backward compatibility
export { formatToOffsetDateTime as formatOffsetDateTime, formatDateForPicker };

type Labels = Record<string, string>;

/**
 * Prospect status steps in order (English keys for status matching)
 */
export const PROSPECT_STATUS_STEPS = [
  'Open',
  'Contacting',
  'Engaged',
  'Quote Generated',
  'Quote Accepted',
  'Ordered',
  'Delivered',
  'Closed',
];

/**
 * Get translated label for a status step
 * @param step - The English step name
 * @param labels - i18n labels from Contentful
 * @returns Translated label or fallback to English
 */
const getStepLabel = (step: string, labels?: Labels): string => {
  const labelMap: Record<string, string> = {
    Open: labels?.['const.PROSPECT_STATUS_STEPS.open'] ?? 'Open',
    Contacting: labels?.['const.PROSPECT_STATUS_STEPS.contacting'] ?? 'Contacting',
    Engaged: labels?.['const.PROSPECT_STATUS_STEPS.engaged'] ?? 'Engaged',
    'Quote Generated': labels?.['const.PROSPECT_STATUS_STEPS.quoteGenerated'] ?? 'Quote Generated',
    'Quote Accepted': labels?.['const.PROSPECT_STATUS_STEPS.quoteAccepted'] ?? 'Quote Accepted',
    Ordered: labels?.['const.PROSPECT_STATUS_STEPS.ordered'] ?? 'Ordered',
    Delivered: labels?.['const.PROSPECT_STATUS_STEPS.delivered'] ?? 'Delivered',
    Closed: labels?.['const.PROSPECT_STATUS_STEPS.closed'] ?? 'Closed',
  };
  return labelMap[step] ?? step;
};

/**
 * Get stepper steps based on current status
 * @param currentStatus - The current status name
 * @param labels - i18n labels from Contentful
 * @returns Array of Step objects for the Stepper component
 */
export const getStepperSteps = (
  currentStatus: string | undefined | null,
  labels?: Labels
): Step[] => {
  // If statusName is null or undefined, default to "Open" (index 0)
  const currentIndex = currentStatus
    ? PROSPECT_STATUS_STEPS.findIndex((step) => step.toLowerCase() === currentStatus.toLowerCase())
    : 0;

  const normalizedStatus = currentStatus?.toLowerCase() || 'open';

  return PROSPECT_STATUS_STEPS.map((stepKey, index) => {
    const label = getStepLabel(stepKey, labels);
    let isDisabled = true; // Default to disabled
    let isHighlighted = false;

    // Determine if step is clickable based on status transition rules
    if (stepKey === 'Open') {
      isDisabled = normalizedStatus !== 'closed';
      isHighlighted = !isDisabled;
    } else if (stepKey === 'Closed') {
      // Closed is clickable from any status except when already Closed or Delivered
      isDisabled = normalizedStatus === 'closed' || normalizedStatus === 'delivered';
      isHighlighted = !isDisabled;
    } else if (stepKey === 'Engaged') {
      // Engaged is clickable only from Open or Contacting
      isDisabled = !(normalizedStatus === 'open' || normalizedStatus === 'contacting');
      isHighlighted = !isDisabled;
    } else if (stepKey === 'Contacting') {
      // Contacting is clickable only from Open
      isDisabled = normalizedStatus !== 'open';
      isHighlighted = !isDisabled;
    }
    // All other steps (Quote Generated, Quote Accepted, Ordered, Delivered) are not clickable

    // Determine step status
    let stepStatus: StepStatus;
    if (index < currentIndex) {
      stepStatus = 'completed';
    } else if (index === currentIndex) {
      // If current status is Closed, show it as completed (with checkmark)
      stepStatus = normalizedStatus === 'closed' && stepKey === 'Closed' ? 'completed' : 'active';
    } else {
      stepStatus = 'pending';
    }

    return {
      id: `step-${index}`,
      label,
      value: stepKey, // English key for status matching
      status: stepStatus,
      disabled: isDisabled,
      highlighted: isHighlighted,
    };
  }) as Step[];
};

export type Theme = 'light' | 'dark';

/**
 * Get the next engagement date from contact tracking or direct prospect field
 * @param prospect - The prospect data
 * @returns Next engagement date in YYYY-MM-DD format or undefined
 * @example
 * ```ts
 * getNextEngagementDate(prospect) // "2024-12-25" or undefined
 * ```
 */
export const getNextEngagementDate = (prospect?: Prospect): string | undefined => {
  // Priority 1: Check active contact tracking
  const activeTracking = prospect?.prospectContactTrackings?.find(
    (tracking) => tracking.isActive === true
  );

  if (activeTracking?.nextToBeContacted) {
    return formatDateForPicker(activeTracking.nextToBeContacted);
  }

  // Priority 2: Check direct nextEngagementDate field on prospect
  if (prospect?.nextEngagementDate) {
    return formatDateForPicker(prospect.nextEngagementDate);
  }

  return undefined;
};

/**
 * Get the latest note content from the notes array sorted by createdAt descending
 * @param notes - Array of prospect notes
 * @returns Latest note content or empty string
 * @example
 * ```ts
 * getLatestNoteContent(prospect.notes) // "Latest note text"
 * ```
 */
export const getLatestNoteContent = (notes?: Note[]): string => {
  if (!notes?.length) return '';

  // Sort by createdAt descending to get the latest one
  const sortedNotes = [...notes].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  return sortedNotes[0]?.noteContent || '';
};

/**
 * Update prospect contact trackings with next engagement date
 * @param prospect - The prospect data
 * @param nextEngagementDate - Next engagement date string (YYYY-MM-DD or MM/DD/YYYY)
 * @param now - Current timestamp in ISO format
 * @returns Updated prospect contact trackings array with only the active tracking
 * @example
 * ```ts
 * updateProspectContactTrackings(prospect, '2026-02-05', now)
 * ```
 */
export const updateProspectContactTrackings = (
  prospect: Prospect,
  nextEngagementDate: string | undefined,
  now: string
) => {
  if (!nextEngagementDate) {
    return prospect.prospectContactTrackings || [];
  }

  const trackings = prospect.prospectContactTrackings || [];

  // Convert date to proper ISO format: YYYY-MM-DDTHH:mm:ssZ (without milliseconds)
  const dateStr = nextEngagementDate;
  const currentTime = new Date();
  let nextToBeContactedDate: string;

  // Check if date is in MM/DD/YYYY format or YYYY-MM-DD format
  if (dateStr.includes('/')) {
    // Parse MM/DD/YYYY format
    const [month, day, year] = dateStr.split('/');
    const dateObj = new Date(
      Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        currentTime.getUTCHours(),
        currentTime.getUTCMinutes(),
        currentTime.getUTCSeconds()
      )
    );
    nextToBeContactedDate = dateObj.toISOString().replace(/\.\d{3}Z$/, 'Z');
  } else {
    // Already in YYYY-MM-DD format
    const dateObj = new Date(
      Date.UTC(
        parseInt(dateStr.split('-')[0]),
        parseInt(dateStr.split('-')[1]) - 1,
        parseInt(dateStr.split('-')[2]),
        currentTime.getUTCHours(),
        currentTime.getUTCMinutes(),
        currentTime.getUTCSeconds()
      )
    );
    nextToBeContactedDate = dateObj.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  const activeTracking = trackings.find((t) => t.isActive === true);

  if (activeTracking) {
    // Return only the updated active tracking object
    return [
      {
        ...activeTracking,
        nextToBeContacted: nextToBeContactedDate,
        updatedDatetime: now,
        updatedBy: 'portal-user',
      },
    ];
  } else {
    // No active tracking exists, create a new one
    return [
      {
        contactedMethod: 'Engagement',
        nextToBeContacted: nextToBeContactedDate,
        isActive: true,
        isDeleted: false,
        createdDatetime: now,
        updatedDatetime: now,
        createdBy: 'portal-user',
        updatedBy: 'portal-user',
      },
    ];
  }
};

/**
 * Build prospect update payload with base fields and optional overrides
 * @param prospect - The prospect data
 * @param now - Current timestamp in ISO format
 * @param overrides - Optional fields to override the base payload
 * @returns Complete prospect update payload
 * @example
 * ```ts
 * buildProspectPayload(prospect, now, { city: 'New York' })
 * ```
 */
export const buildProspectPayload = (
  prospect: Prospect,
  now: string,
  overrides: Partial<Prospect> = {}
): Prospect => {
  return {
    ...prospect,

    // Consent defaults
    isEmailOptIn: prospect.isEmailOptIn ?? true,
    isCallOptIn: prospect.isCallOptIn ?? false,
    isPrivacyConsent: prospect.isPrivacyConsent ?? true,

    // Field aliases (API response may use legacy names)
    noteText: prospect.noteText || prospect.situationalNotes,
    prospectSourceName: prospect.prospectSourceName || prospect.sourceName || '',
    prospectCaptureForm: prospect.prospectCaptureForm || prospect.captureForm || '',
    createdDatetime: prospect.createdDatetime || prospect.createdDate,

    // Defaults for missing values
    timezone: prospect.timezone || 'UTC',
    prospectType: prospect.prospectType || 'Lead',
    recordType: prospect.recordType || 'Lead',
    prospectScores: prospect.prospectScores || [],
    prospectStatusLogs: prospect.prospectStatusLogs || [],
    testDriveSchedules: prospect.testDriveSchedules || [],
    prospectContactTrackings: prospect.prospectContactTrackings || [],

    // Audit fields
    isDeleted: false,
    updatedDatetime: now,
    updatedBy: 'portal-user',

    ...overrides,
  };
};

/**
 * Find the status log entry describing the prospect's closure
 * @param prospect - The prospect data
 * @returns The closed status log entry, or undefined if the prospect has none
 * @example
 * ```ts
 * getClosedStatusLog(prospect) // { statusName: 'Closed', subStatusName: 'junk', ... }
 * ```
 */
export const getClosedStatusLog = (prospect?: Prospect): ProspectStatusLog | undefined => {
  const closedLogs = prospect?.prospectStatusLogs?.filter(
    (log) => log.statusName?.toLowerCase() === 'closed'
  );

  if (!closedLogs?.length) return undefined;

  // Prefer the currently active closed entry (no end date yet)
  const activeLog = closedLogs.find((log) => log.endDatetime == null);
  if (activeLog) return activeLog;

  // Otherwise fall back to the most recently started closed entry
  return [...closedLogs].sort((a, b) => {
    const dateA = a.startDatetime ? new Date(a.startDatetime).getTime() : 0;
    const dateB = b.startDatetime ? new Date(b.startDatetime).getTime() : 0;
    return dateB - dateA;
  })[0];
};

const NOT_AVAILABLE_FALLBACK = 'Not available';

/**
 * Resolve the display label for a closed sub-status value
 * @param subStatusName - Raw sub-status value from the status log
 * @param labels - i18n labels from Contentful
 * @returns Matching label or "Not available" when unrecognized/missing
 */
export const resolveClosedSubStatusLabel = (
  subStatusName: string | undefined | null,
  labels?: Labels
): string => {
  const notAvailable = labels?.['details.notAvailable'] ?? NOT_AVAILABLE_FALLBACK;
  if (!subStatusName) return notAvailable;

  const match = getClosedSubStatusOptions(labels).find((option) => option.value === subStatusName);
  return match?.label ?? notAvailable;
};

/**
 * Resolve the display label for a primary close reason value
 * @param primaryReasonName - Raw primary reason value from the status log
 * @param labels - i18n labels from Contentful
 * @returns Matching label or "Not available" when unrecognized/missing
 */
export const resolvePrimaryReasonLabel = (
  primaryReasonName: string | undefined | null,
  labels?: Labels
): string => {
  const notAvailable = labels?.['details.notAvailable'] ?? NOT_AVAILABLE_FALLBACK;
  if (!primaryReasonName) return notAvailable;

  const allPrimaryReasons = [
    ...getJunkPrimaryReasons(labels),
    ...getNotInterestedPrimaryReasons(labels),
  ];
  const match = allPrimaryReasons.find((option) => option.value === primaryReasonName);
  return match?.label ?? notAvailable;
};

/**
 * Resolve the display label for a secondary close reason value
 * @param primaryReasonName - Raw primary reason value from the status log (determines the lookup group)
 * @param secondaryReasonName - Raw secondary reason value from the status log
 * @param labels - i18n labels from Contentful
 * @returns Matching label or "Not available" when unrecognized/missing
 */
export const resolveSecondaryReasonLabel = (
  primaryReasonName: string | undefined | null,
  secondaryReasonName: string | undefined | null,
  labels?: Labels
): string => {
  const notAvailable = labels?.['details.notAvailable'] ?? NOT_AVAILABLE_FALLBACK;
  if (!primaryReasonName || !secondaryReasonName) return notAvailable;

  const secondaryReasons = getSecondaryReasonsByPrimary(labels)[primaryReasonName];
  const match = secondaryReasons?.find((option) => option.value === secondaryReasonName);
  return match?.label ?? notAvailable;
};
